/**
 * © 2025 Indikin.online. All rights reserved.
 * Made with❤️ by YalaCoder.
 *
 * --- REWRITE SUMMARY (V7 - Performance Optimization: Bundle Default Lang) ---
 * - MISSION: To fix the "black screen" render-blocking issue caused by
 * waiting for the network to fetch translations.
 *
 * - THE FIX:
 * 1.  IMPORTED `translation.json` (English) directly at the top level.
 * 2.  ADDED a `resources` object to `i18n.init()`.
 * 3.  CONFIGURED 'multiload' behavior:
 *     - If the language is 'en', 'en-US', or 'en-AU', it uses the BUNDLED resource (instant).
 *     - If the language is anything else (e.g., 'es', 'fr'), it uses the
 *       HTTP BACKEND to fetch the file (lazy loaded).
 *
 * - RESULT: The application now renders immediately for the vast majority
 * of users (English speakers) without showing a blocking global loader.
 * Secondary languages still strictly load on demand to keep the bundle small.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';

// --- PERFORMANCE OPTIMIZATION: Bundle default English translations ---
// This prevents the "Black Screen" (Global Suspense) on initial load by
// eliminating the network request for the default language.
import enTranslation from './locales/en/translation.json';

// --- Supported Languages ---
const supportedLngs = ['en', 'en-AU', 'es', 'fr', 'de', 'pt', 'ru', 'ar', 'zh', 'ja', 'hi'];

// Interop for Legacy Builds (SystemJS/CommonJS mismatch)
const i18nInstance = i18n.default || i18n;

i18nInstance
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    supportedLngs: supportedLngs,
    debug: false, // SILENCE: Reduced console noise in Development
    returnNull: false,
    returnEmptyString: false,

    // --- HYBRID STRATEGY: Bundled + Network ---
    resources: {
      en: { translation: enTranslation },
      // 'en-US' removed to rely on 'en' fallback and avoid UI redundancy.
      // 'en-AU' removed to allow fetching specific translation from /locales/en-AU/
    },
    // The backend plugin will still run for other languages not in `resources`.

    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },

    detection: {
      order: ['querystring', 'cookie', 'localStorage', 'navigator', 'htmlTag'],
      caches: ['cookie', 'localStorage'],
      cookieOptions: {
        path: '/',
        sameSite: 'Lax',
        secure: true,
      },
    },

    interpolation: {
      escapeValue: false,
    },

    // --- IMPORTANT: Optimization Flags ---
    // If the language is in `resources`, do not suspend.
    react: {
      useSuspense: true, // Keep true for other languages, but bundled ones won't trigger it.
    },
    // Prevent backend from trying to load 'en' since we provided it.
    partialBundledLanguages: true,
  });

// --- GUARD: Prevent auto-switching to hidden languages ---
// Russian is hidden from the UI but kept in supportedLngs so that
// users who manually set ?lng=ru can still access it.
if (typeof window !== 'undefined') {
  const HIDDEN_LANGUAGES = ['ru'];
  const urlParams = new URLSearchParams(window.location.search);
  const explicitLng = urlParams.get('lng');

  if (HIDDEN_LANGUAGES.includes(i18nInstance.language) && !explicitLng) {
    // Language was auto-detected (via navigator, cookie, or localStorage)
    // but is hidden from the UI — force fallback to English
    i18nInstance.changeLanguage('en');
  }
}

export default i18n;
